import React, { useContext, useEffect, useState } from "react";
import { Modal, Box, Typography, Alert, Divider, IconButton } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { ArrowBack } from "@mui/icons-material";
import Button from "../components/Button";
import UserContext from "../assets/user_context";
import { useNavigate } from "react-router-dom";
import { OrgSelector } from "./components/OrgSelector";
import { AddOrgForm } from "../locations/onboarding/add_org";

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.25),
  boxShadow: theme.shadows[24],
  width: "max-content",
  minWidth: "500px",
  maxWidth: "600px",
}));

const ModalInner = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(5, 4, 5, 4),
  width: "100%",
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const BrokenOrgModal = () => {
  const {
    setOrg,
    userData,
    brokenOrgDetected,
    setBrokenOrgDetected,
    brokenOrgId,
    authHook,
    user,
    inviteCheckState,
    markManualModalClose,
  } = useContext(UserContext);

  const theme = useTheme();
  const navigate = useNavigate();

  // Check if we're in a browser environment
  const [isBrowser, setIsBrowser] = useState(false);


  // State for AddOrg modal
  const [showAddOrg, setShowAddOrg] = useState(false);

  // State for selected org to switch to
  const [selectedOrgForSwitch, setSelectedOrgForSwitch] = useState(null);

  useEffect(() => {
    setIsBrowser(
      typeof window !== "undefined" && typeof document !== "undefined",
    );
  }, []);

  // Get broken org name
  const brokenOrgName = brokenOrgDetected && brokenOrgId
    ? userData?.orgs?.find((o) => o.orgID === brokenOrgId)?.orgName || "Unknown"
    : null;

  // Check if user has only one org (the broken one)
  const hasOnlyBrokenOrg = brokenOrgDetected && userData?.orgs?.length === 1;

  // Check if user has other orgs to switch to
  const hasOtherOrgs = userData?.orgs && userData.orgs.length > 1;

  // Initialize selected org for switch (first non-broken org)
  useEffect(() => {
    if (hasOtherOrgs && !selectedOrgForSwitch) {
      const firstOtherOrg = userData.orgs.find(o => o.orgID !== brokenOrgId);
      if (firstOtherOrg) {
        setSelectedOrgForSwitch(firstOtherOrg.orgID);
      }
    }
  }, [hasOtherOrgs, selectedOrgForSwitch, userData?.orgs, brokenOrgId]);

  const handleSwitchOrg = () => {
    if (!selectedOrgForSwitch) return;

    // Mark that user manually interacted with modal
    markManualModalClose();

    // Clear broken org state
    setBrokenOrgDetected(false);

    // Navigate and switch org
    navigate("/");
    setOrg(selectedOrgForSwitch);
  };

  const handleCreateOrg = () => {
    setShowAddOrg(true);
  };

  const handleCloseAddOrg = () => {
    setShowAddOrg(false);
    // Note: After org creation, AddOrg will call setOrg with new org
    // which will trigger org load, and if successful, broken state will clear automatically
  };

  const handleModalClose = () => {
    // Only allow closing if user has other orgs
    if (!hasOnlyBrokenOrg) {
      markManualModalClose();
      setBrokenOrgDetected(false);
    }
  };

  // Don't render Modal until we're in browser environment
  if (!isBrowser) {
    return null;
  }

  return (
    <Modal
      open={brokenOrgDetected}
      onClose={() => {}} // Prevent closing by clicking outside - user must use buttons
      disableEscapeKeyDown={true}
    >
      <ModalBox>
        <ModalInner>
          {showAddOrg ? (
            // Show create org form
            <>
              {hasOtherOrgs && (
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <IconButton
                    onClick={handleCloseAddOrg}
                    sx={{ mr: 1 }}
                    size="small"
                  >
                    <ArrowBack />
                  </IconButton>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Back to options
                  </Typography>
                </Box>
              )}
              {authHook?.db && (
                <AddOrgForm
                  db={authHook.db}
                  userData={userData}
                  user={user}
                  closeModal={handleCloseAddOrg}
                  inviteCheckState={inviteCheckState}
                />
              )}
            </>
          ) : (
            // Show broken org options
            <>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  mb: 3,
                  color: theme.palette.text.primary,
                  textAlign: "center",
                }}
              >
                Organization Data Issue
              </Typography>

              <Alert severity="error" sx={{ mb: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Missing Required Data - Need Help? Contact{" "}
                  <a
                    href="mailto:support@t3books.com"
                    style={{
                      color: "inherit",
                      textDecoration: "underline",
                    }}
                  >
                    support@t3books.com
                  </a>
                </Typography>
                <Typography variant="body2">
                  The organization "{brokenOrgName}" is missing required data (accounts, funds, or contacts) and cannot be loaded properly.
                </Typography>
              </Alert>

              {hasOtherOrgs && (
                <Section>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: theme.palette.text.primary }}
                  >
                    Switch to Another Organization
                  </Typography>
                  <OrgSelector
                    userData={userData}
                    selectedOrg={selectedOrgForSwitch}
                    onOrgChange={setSelectedOrgForSwitch}
                    disabled={false}
                    excludeOrgs={[brokenOrgId]}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSwitchOrg}
                    disabled={!selectedOrgForSwitch}
                    fullWidth
                  >
                    Switch Organization
                  </Button>
                  <Divider sx={{ my: 3 }} />
                </Section>
              )}

              <Section>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: theme.palette.text.primary }}
                >
                  {hasOtherOrgs ? "Or Create New Organization" : "Create New Organization"}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleCreateOrg}
                  fullWidth
                >
                  Create New Organization
                </Button>
              </Section>
            </>
          )}
        </ModalInner>
      </ModalBox>
    </Modal>
  );
};

export default BrokenOrgModal;
